import React, { useState, useRef, useEffect } from "react";
import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import gregorian from "react-date-object/calendars/gregorian";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import api from '../../api/api';

const AddMeetingModal = ({ isOpen, onClose, initialData, onAdd, readOnly = false }) => {
  const [meetingTitle, setMeetingTitle] = useState(initialData?.title || "");
  const [description, setDescription] = useState("");
  const [meetingDate, setMeetingDate] = useState(null);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("09:00");
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isStartOpen, setIsStartOpen] = useState(false);
  const [isEndOpen, setIsEndOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meetingType, setMeetingType] = useState("حضوری داخل دانشگاه");
  const [locationOrLink, setLocationOrLink] = useState(initialData?.locationorlink || "");
  const isEditing = Boolean(initialData?.id);
  const isReadOnly = readOnly || (isEditing && initialData?.can_edit !== true);

  const searchRef = useRef(null);
  const startRef = useRef(null);
  const endRef = useRef(null);

  const timeSlots = [
    "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
    "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00"
  ];

  useEffect(() => {
    if (!initialData) return;
    setMeetingTitle(initialData.title || "");
    setDescription(initialData.description || "");
    setLocationOrLink(initialData.locationorlink || "");
    setMeetingType(initialData.type || "حضوری داخل دانشگاه");
    setSelectedParticipants((initialData.participants || []).map((userId) => {
      const user = allUsers.find((candidate) => candidate.id === userId);
      return user || { id: userId, name: `کاربر ${userId}`, avatar: '' };
    }));
    if (initialData.scheduled_at) {
      const scheduled = new Date(initialData.scheduled_at);
      setMeetingDate(new DateObject({ date: scheduled, calendar: gregorian }).convert(persian, persian_fa));
      setStartTime(scheduled.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    }
    if (initialData.end_at) {
      setEndTime(new Date(initialData.end_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    }
  }, [initialData, allUsers]);

  // Fetch users when modal opens
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isOpen) return;
      setLoading(true);
      try {
        const res = await api.get('/users/');
        setAllUsers(res.data.map(u => ({
          id: u.id,
          name: u.name || u.username,
          role: u.is_staff ? 'کارمند' : 'کاربر',
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || u.username)}&background=random&color=fff&size=128`
        })));
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [isOpen]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) setIsSearchOpen(false);
      if (startRef.current && !startRef.current.contains(event.target)) setIsStartOpen(false);
      if (endRef.current && !endRef.current.contains(event.target)) setIsEndOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const filteredUsers = allUsers.filter(u =>
    u.name.includes(searchQuery) && !selectedParticipants.some(s => s.id === u.id)
  );

  const isOnlineMeeting = meetingType === "آنلاین";

  const getMeetingDateTime = (time) => {
    if (!meetingDate) return null;
    const date = meetingDate.toDate();
    const [hours, minutes] = time.split(':').map(Number);
    date.setHours(hours, minutes, 0, 0);
    return date.toISOString();
  };

  const getMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  if (isReadOnly) return;

  if (!meetingTitle.trim()) return alert("لطفاً عنوان جلسه را وارد کنید.");
  if (!isEditing && selectedParticipants.length === 0) return alert("لطفاً حداقل یک عضو انتخاب کنید.");
  if (!locationOrLink.trim()) {
    return alert(isOnlineMeeting ? "لطفاً لینک جلسه را وارد کنید." : "لطفاً آدرس جلسه را وارد کنید.");
  }
  if (getMinutes(endTime) <= getMinutes(startTime)) {
    return alert("ساعت پایان جلسه باید بعد از ساعت شروع باشد.");
  }

  const newMeeting = {
    title: meetingTitle,
    description: description,
    scheduled_at: getMeetingDateTime(startTime),
    end_at: getMeetingDateTime(endTime),
    type: meetingType,
    locationorlink: locationOrLink.trim(),
    is_mandatory: false,
    participants_ids: selectedParticipants.map(p => p.id)
  };

  try {
    if (onAdd) {
      await onAdd(newMeeting);
    } else {
      await api.post('/meetings/', newMeeting);
    }
    onClose();
  } catch (error) {
    console.error('❌ خطا در ساخت جلسه:', error);
    alert("خطا در ساخت جلسه. لطفاً دوباره تلاش کنید.");
  }
};

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" dir="rtl">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs" onClick={onClose} />
      <div className="bg-white w-full max-w-[650px] rounded-3xl p-6 shadow-2xl relative z-[10000]">
        <button onClick={onClose} className="text-slate-400 hover:text-rose-500 mb-4 cursor-pointer">✕</button>
        <h2 className="text-lg font-bold text-slate-800 mb-4">{isReadOnly ? 'جزئیات جلسه' : isEditing ? 'ویرایش جلسه' : 'ایجاد جلسه ی جدید'}</h2>

        {loading && <div className="text-center py-2 text-sm text-slate-500">در حال بارگذاری کاربران...</div>}

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* عنوان */}
          <div className="grid grid-cols-[80px_1fr] items-center gap-2">
            <label className="text-sm font-bold">عنوان:</label>
            <input disabled={isReadOnly} value={meetingTitle} onChange={(e) => setMeetingTitle(e.target.value)} type="text" className="w-full px-4 py-2 text-sm bg-slate-50 border rounded-xl outline-none focus:border-indigo-500 disabled:bg-slate-100" />
          </div>

          {/* توضیحات */}
          <div className="grid grid-cols-[80px_1fr] items-start gap-2">
            <label className="text-sm font-bold mt-2">توضیحات:</label>
            <textarea disabled={isReadOnly} value={description} onChange={(e) => setDescription(e.target.value)} rows="3" className="w-full px-4 py-2 text-sm bg-slate-50 border rounded-xl outline-none focus:border-indigo-500 resize-none disabled:bg-slate-100" />
          </div>
         
         <select
  value={meetingType}
  onChange={(e) => setMeetingType(e.target.value)}
  disabled={isReadOnly}
  className="w-full px-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none"
>
  <option value="حضوری داخل دانشگاه">حضوری داخل دانشگاه</option>
  <option value="حضوری خارج دانشگاه">حضوری خارج دانشگاه</option>
  <option value="آنلاین">آنلاین</option>
  <option value="سایر">سایر</option>
</select>

          {/* لینک (آنلاین) یا آدرس (حضوری/سایر) */}
          <div className="grid grid-cols-[80px_1fr] items-center gap-2">
            <label className="text-sm font-bold">
              {isOnlineMeeting ? "لینک جلسه:" : "آدرس:"}
            </label>
            <input
              value={locationOrLink}
              onChange={(e) => setLocationOrLink(e.target.value)}
              disabled={isReadOnly}
              type="text"
              dir={isOnlineMeeting ? "ltr" : "rtl"}
              placeholder={isOnlineMeeting ? "https://..." : "آدرس محل برگزاری جلسه"}
              className="w-full px-4 py-2 text-sm bg-slate-50 border rounded-xl outline-none focus:border-indigo-500"
            />
          </div>

          {/* انتخاب اعضا */}
          <div className="grid grid-cols-[80px_1fr] items-start gap-2 relative" ref={searchRef}>
            <label className="text-sm font-bold mt-2">اعضا:</label>
            <div className="w-full flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" disabled={isReadOnly} onClick={() => setIsSearchOpen(!isSearchOpen)} className="w-10 h-10 rounded-full bg-indigo-50 border text-indigo-600 font-bold disabled:opacity-50">+</button>
                {selectedParticipants.map(user => (
                  <div key={user.id} onClick={() => !isReadOnly && setSelectedParticipants(selectedParticipants.filter(s => s.id !== user.id))} className={`flex items-center gap-2 pr-1 pl-3 py-1 bg-white border rounded-full ${isReadOnly ? '' : 'cursor-pointer'}`}>
                    <img src={user.avatar} alt={user.name} className="w-7 h-7 rounded-full" />
                    <span className="text-xs font-bold">{user.name}</span>
                  </div>
                ))}
              </div>
              {isSearchOpen && (
                <div className="absolute top-full right-0 w-64 mt-2 bg-white border shadow-xl rounded-2xl z-50 max-h-60 overflow-y-auto">
                  <input disabled={isReadOnly} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="جستجو..." className="w-full p-3 text-xs border-b outline-none" />
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map(user => (
                      <div key={user.id} onClick={() => !isReadOnly && (() => { setSelectedParticipants([...selectedParticipants, user]); setIsSearchOpen(false); })()} className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3">
                        <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
                        <span className="text-sm">{user.name}</span>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-xs text-slate-400 text-center">هیچ کاربری یافت نشد</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* تاریخ و زمان */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <DatePicker calendar={persian} locale={persian_fa} value={meetingDate} onChange={isReadOnly ? undefined : setMeetingDate} disabled={isReadOnly} inputClass="w-full px-3 py-2 text-xs border rounded-xl" placeholder="تاریخ" />
            <div className="relative" ref={startRef}>
              <button type="button" disabled={isReadOnly} onClick={() => setIsStartOpen(!isStartOpen)} className="w-full py-2 text-xs border rounded-xl disabled:bg-slate-100">{startTime} ▾</button>
                  {isStartOpen && <div className="absolute w-full bg-white border rounded-xl max-h-40 overflow-y-auto">{timeSlots.map(t => <button key={t} type="button" disabled={isReadOnly} onClick={() => {setStartTime(t); setIsStartOpen(false)}} className="block w-full p-2 text-xs hover:bg-slate-100 disabled:opacity-50">{t}</button>)}</div>}
            </div>
            <div className="relative" ref={endRef}>
              <button type="button" disabled={isReadOnly} onClick={() => setIsEndOpen(!isEndOpen)} className="w-full py-2 text-xs border rounded-xl disabled:bg-slate-100">{endTime} ▾</button>
                  {isEndOpen && <div className="absolute w-full bg-white border rounded-xl max-h-40 overflow-y-auto">{timeSlots.map(t => <button key={t} type="button" disabled={isReadOnly} onClick={() => {setEndTime(t); setIsEndOpen(false)}} className="block w-full p-2 text-xs hover:bg-slate-100 disabled:opacity-50">{t}</button>)}</div>}
            </div>
          </div>

          {!isReadOnly && <button type="submit" className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700">ذخیره جلسه</button>}
        </form>
      </div>
    </div>
  );
};

export default AddMeetingModal;